import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, Clock, FileText, Plus, ChevronLeft, ChevronRight, Calendar as CalIcon, Trash2, CheckCircle2, Circle, Bell, BellOff, Settings, Mic, Send } from 'lucide-react';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { getStoredUserName } from './Azurechatbot';
import { CalendarSyncModal, handleOAuthCallback, mergeExternalEvents } from './CalendarSyncModal';
import { loadCalendarFromStorage, updateEventCompletion, deleteEvent,saveCalendarToStorage } from './schedulerHandlers';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import VoiceRealtimeManager from './VoiceRealtimeManager';
import BigCalendarPlannerVoiceManager from './BigCalendarPlannerVoiceManager';
import BigCalendarPlannerChatManager from './BigCalendarPlannerChatManager';
// Notification helper functions
const requestNotificationPermission = async () => {
  if (!Capacitor.isNativePlatform()) return true;
  
  try {
    const permission = await LocalNotifications.requestPermissions();
    return permission.display === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

const scheduleEventNotification = async (event, dateKey, eventIndex, userName) => {
  if (!Capacitor.isNativePlatform()) {
    console.log('Notifications only work on mobile');
    return null;
  }

  try {
    const eventTime = parseTimeToMinutes(event.time);
    if (eventTime === null) {
      console.log('⚠️ No valid time for notification');
      return null;
    }

    // ✅ Parse dateKey properly (YYYY-MM-DD format)
    const [year, month, day] = dateKey.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day); // month is 0-indexed
    
    // Set event time
    const eventHours = Math.floor(eventTime / 60);
    const eventMinutes = eventTime % 60;
    eventDate.setHours(eventHours, eventMinutes, 0, 0);
    
    const now = new Date();
    const timeUntilEvent = eventDate.getTime() - now.getTime();
    const minutesUntilEvent = Math.floor(timeUntilEvent / (60 * 1000));
    
    // ✅ SMART TIMING: Choose notification delay based on how far away event is
    let notificationDelay; // in milliseconds
    let notificationMessage;
    
    if (minutesUntilEvent <= 0) {
      console.log('⏰ Event time has passed, skipping notification');
      return null;
    } 
    else if (minutesUntilEvent <= 1) {
      // Event is 1 minute away or less - notify immediately
      notificationDelay = 0;
      notificationMessage = `"${event.text}" is starting now!`;
      console.log('⏰ Event is imminent - notifying immediately');
    }
    else if (minutesUntilEvent <= 2) {
      // Event is 2 minutes away - notify 1 minute before
      notificationDelay = 1 * 60 * 1000; // 1 minute
      notificationMessage = `"${event.text}" is in 1 minute!`;
      console.log('⏰ Event is very soon - notifying 1 minute before');
    }
    else if (minutesUntilEvent <= 5) {
      // Event is 3-5 minutes away - notify 1 minute before
      notificationDelay = (minutesUntilEvent - 1) * 60 * 1000;
      notificationMessage = `"${event.text}" is in 1 minute!`;
      console.log(`⏰ Event is ${minutesUntilEvent} minutes away - notifying 1 minute before`);
    }
    else {
      // Event is more than 5 minutes away - notify 5 minutes before
      notificationDelay = 5 * 60 * 1000; // 5 minutes
      notificationMessage = `"${event.text}" is in 5 minutes!`;
      console.log(`⏰ Event is ${minutesUntilEvent} minutes away - notifying 5 minutes before`);
    }
    
    const notificationDate = new Date(eventDate.getTime() - notificationDelay);
    
    // Double-check notification time hasn't passed
    if (notificationDate <= now) {
      console.log('⏰ Notification time has passed, notifying immediately instead');
      notificationDate.setTime(now.getTime() + 1000); // 1 second from now
      notificationMessage = `"${event.text}" is starting now!`;
    }

    console.log(`⏰ Notification will fire at ${notificationDate.toLocaleString()}`);
    console.log(`   Event is at ${eventDate.toLocaleString()}`);
    console.log(`   Message: "${notificationMessage}"`);

    const notificationId = parseInt(`${year}${month}${day}${eventIndex}${Date.now().toString().slice(-6)}`);
    
    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: `Event Reminder`,
          body: `Hey ${userName}, ${notificationMessage}`,
          schedule: { at: notificationDate },
          sound: 'default',
          smallIcon: 'ic_notification',
          iconColor: '#ffffffff',
          extra: {
            eventId: `${dateKey}-${eventIndex}`,
            eventTitle: event.text,
            eventTime: event.time
          }
        }
      ]
    });

    console.log(`✅ Notification scheduled successfully`);
    return notificationId;
  } catch (error) {
    console.error('❌ Error scheduling notification:', error);
    return null;
  }
};


const cancelEventNotification = async (notificationId) => {
  if (!Capacitor.isNativePlatform() || !notificationId) return;
  
  try {
    await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
    console.log(`✅ Notification ${notificationId} cancelled`);
  } catch (error) {
    console.error('Error cancelling notification:', error);
  }
};

const formatTime12Hour = (timeStr) => {
  if (!timeStr || timeStr === 'All day') return 'All day';
  
  // Match various time formats: "4:05am", "4:05 am", "4am", "4 am", "4:05", etc.
  const timeMatch = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (!timeMatch) return timeStr;
  
  let hours = parseInt(timeMatch[1]);
  const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0; // ✅ DEFAULT 0, NOT IGNORE
  const meridian = timeMatch[3];
  
  if (meridian) {
    // User provided AM/PM, use it
    return `${hours}:${minutes.toString().padStart(2, '0')} ${meridian.toUpperCase()}`;
  }
  
  // No meridian provided, assume 24-hour format and convert
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};


const formatDate = (date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
};

// ============================================
// IMPROVED: Parse Time to Minutes (with minutes!)
// ============================================

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || timeStr === 'All day') return null;
  
  // Match: "4:05am", "4:05 am", "4am", "4 am"
  const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (!match) return null;
  
  let hours = parseInt(match[1]);
  const minutes = match[2] ? parseInt(match[2]) : 0; // ✅ PRESERVE MINUTES - was the bug!
  const meridian = match[3]?.toUpperCase();
  
  // Handle 12-hour format
  if (meridian === 'PM' && hours !== 12) hours += 12;
  if (meridian === 'AM' && hours === 12) hours = 0;
  
  return hours * 60 + minutes; // ✅ NOW INCLUDES MINUTES
};


const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
};


export default function GlassyCalendar({ firestoreService }) {
  // IMPORTANT: Declare externalEvents BEFORE using it in useMemo
  const [micMuted, setMicMuted] = useState(false);
  const [externalEvents, setExternalEvents] = useState([]);
  const [manualTasks, setManualTasks] = useState(() => loadCalendarFromStorage());
const [chatManager, setChatManager] = useState(null);
const [isSendingMessage, setIsSendingMessage] = useState(false);
const [messageTimeoutId, setMessageTimeoutId] = useState(null);

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const parseRecurringPattern = (description) => {
  const text = description.toLowerCase();
  
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayMatch = days.find(day => text.includes(`every ${day}`) || text.includes(`on ${day}s`));
  
  if (dayMatch) {
    return {
      type: 'weekly',
      dayOfWeek: days.indexOf(dayMatch),
      dayName: dayMatch,
      interval: 1
    };
  }
  
  if (text.includes('every day') || text.includes('daily')) {
    return { type: 'daily', interval: 1 };
  }
  
  return null;
};

const generateRecurringInstances = (baseEvent, pattern, startDate, weeksAhead = 8) => {
  const instances = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  
  const endDate = new Date(current);
  endDate.setDate(endDate.getDate() + (weeksAhead * 7));
  
  if (pattern.type === 'weekly') {
    while (current <= endDate) {
      if (current.getDay() === pattern.dayOfWeek) {
        const dateKey = formatDateKey(current);
        instances.push({
          dateKey,
          event: { ...baseEvent, isRecurring: true, instanceDate: dateKey }
        });
      }
      current.setDate(current.getDate() + 1);
    }
  }
  
  return instances;
};

const parseRelativeDate = (dateString) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (!dateString || dateString === 'today') return today;
  
  if (dateString === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  }
  
  // Handle day names like "friday"
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayIndex = days.findIndex(day => dateString.toLowerCase() === day);
  
  if (dayIndex !== -1) {
    const currentDay = today.getDay();
    let daysToAdd = dayIndex - currentDay;
    if (daysToAdd < 0) daysToAdd += 7;
    
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysToAdd);
    return targetDate;
  }
  
  return today;
};
const [animatingEvents, setAnimatingEvents] = useState({});
const [showAddEditModal, setShowAddEditModal] = useState(false);
const [formData, setFormData] = useState({
  title: '',
  description: '',
  hours: 9,
  minutes: 0,
  ampm: 'AM'
});
const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
const [selectedDateForAdd, setSelectedDateForAdd] = useState(null);
const [isEditMode, setIsEditMode] = useState(false);
const openAddModal = (date) => {
  setIsEditMode(false);
  setSelectedDateForAdd(date);
  setFormData({
    title: '',
    description: '',
    hours: 9,
    minutes: 0,
    ampm: 'AM'
  });
  setShowAddEditModal(true);
};

const openEditModal = (event) => {
  setIsEditMode(true);
  setSelectedEvent(event);
  
  // Parse existing time
  const timeMatch = event.time?.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (timeMatch) {
    setFormData({
      title: event.text,
      description: event.description || '',
      hours: parseInt(timeMatch[1]),
      minutes: parseInt(timeMatch[2]),
      ampm: timeMatch[3].toUpperCase()
    });
  } else {
    setFormData({
      title: event.text,
      description: event.description || '',
      hours: 9,
      minutes: 0,
      ampm: 'AM'
    });
  }
  
  setShowAddEditModal(true);
  setShowEventDetailsModal(false);
};

const handleSaveEvent = async () => {
  if (!formData.title.trim()) {
    alert('Please enter an event title');
    return;
  }

  const timeStr = `${formData.hours}:${formData.minutes.toString().padStart(2, '0')} ${formData.ampm}`;
  
  if (isEditMode && selectedEvent) {
    // ===== EDIT MODE =====
    const updated = { ...manualTasks };
    updated[selectedEvent.dateKey] = updated[selectedEvent.dateKey].map((task, idx) =>
      idx === selectedEvent.index
        ? { ...task, text: formData.title, description: formData.description, time: timeStr }
        : task
    );
    setManualTasks(updated);
    
    if (firestoreService?.isReady?.()) {
      await saveCalendarToStorage(updated, firestoreService);
    }
  } else {
    // ===== ADD MODE =====
    const dateKey = formatDateKey(selectedDateForAdd);
    
    // ✅ CHECK: Is this a recurring event?
    const fullDescription = `${formData.title} ${formData.description || ''}`.toLowerCase();
    const recurringPattern = parseRecurringPattern(fullDescription);
    
    if (recurringPattern) {
      // ✅ RECURRING EVENT
      console.log('🔄 Creating recurring event:', recurringPattern);
      
      const recurrenceId = `recurring_${Date.now()}`;
      const baseEvent = {
        text: formData.title,
        time: timeStr,
        description: formData.description,
        completed: false,
        notifyEnabled: true,
        notificationId: null,
        isRecurring: true,
        recurrenceId: recurrenceId,
        recurrencePattern: recurringPattern,
        createdAt: new Date().toISOString()
      };
      
      const instances = generateRecurringInstances(baseEvent, recurringPattern, selectedDateForAdd, 8);
      console.log(`✅ Generated ${instances.length} recurring instances`);
      
      const updatedTasks = { ...manualTasks };
      
      instances.forEach(({ dateKey: instDateKey, event }) => {
        updatedTasks[instDateKey] = [...(updatedTasks[instDateKey] || []), event];
      });
      
      setManualTasks(updatedTasks);
      
      if (firestoreService?.isReady?.()) {
        await saveCalendarToStorage(updatedTasks, firestoreService);
      }
      
      alert(`Created ${instances.length} recurring events!`);
      
    } else {
      // ✅ SINGLE EVENT
      const newEvent = {
        text: formData.title,
        time: timeStr,
        description: formData.description,
        completed: false,
        notifyEnabled: true,
        notificationId: null,
        createdAt: new Date().toISOString()
      };
      
      const updatedTasks = {
        ...manualTasks,
        [dateKey]: [...(manualTasks[dateKey] || []), newEvent]
      };
      
      setManualTasks(updatedTasks);
      
      if (firestoreService?.isReady?.()) {
        await saveCalendarToStorage(updatedTasks, firestoreService);
      }
    }
  }

  setShowAddEditModal(false);
  setFormData({
    title: '',
    description: '',
    hours: 9,
    minutes: 0,
    ampm: 'AM'
  });
};
  // NOW we can use externalEvents in the useMemo hook
const tasksByDate = useMemo(() => {
  const merged = { ...manualTasks };
  
  // Merge external events
  externalEvents.forEach(event => {
    const dateKey = new Date(event.start).getDate();
    if (!merged[dateKey]) {
      merged[dateKey] = [];
    }
    merged[dateKey].push(event);
  });
  
  return merged;
}, [manualTasks, externalEvents]);

  // All other state declarations
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDragModal, setShowDragModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showSyncModal, setShowSyncModal] = useState(false);
   const [isLoading, setIsLoading] = useState(true);
const [notificationUserName, setNotificationUserName] = useState(null);



// 3️⃣ ADD STATE VARIABLES (after your existing useState declarations)
const [isVoiceMode, setIsVoiceMode] = useState(false);
const [isInitializingVoice, setIsInitializingVoice] = useState(false);
const [voiceStatus, setVoiceStatus] = useState('');
const [voiceManager, setVoiceManager] = useState(null);
const [voiceMessages, setVoiceMessages] = useState([]);
const [waveformData, setWaveformData] = useState({ points: '', isActive: false, height: 0 });
const [inputValue, setInputValue] = useState('');

const triggerEventAnimation = (dateKey, index, type = 'add') => {
  const animKey = `${dateKey}-${index}-${type}`;
  
  // ✅ Add animation to state
  setAnimatingEvents(prev => ({
    ...prev,
    [animKey]: Date.now()
  }));
  
  // ✅ DETERMINE DURATION BASED ON ANIMATION TYPE
  let duration;
  switch (type) {
    case 'delete':
      duration = 500; // Fade out
      break;
    case 'update':
      duration = 600; // Wiggle
      break;
    case 'add':
    default:
      duration = 600; // Bounce
      break;
  }
  
  console.log(`🎬 Animation: ${type} for ${duration}ms`);
  
  // ✅ Clean up animation after it completes
  const timeoutId = setTimeout(() => {
    setAnimatingEvents(prev => {
      const updated = { ...prev };
      delete updated[animKey];
      return updated;
    });
    console.log(`✅ Animation cleanup: ${animKey}`);
  }, duration);
  
  // ✅ IMPORTANT: Store timeout ID in case we need to cancel
  return timeoutId;
};



useEffect(() => {
  if (!chatManager && notificationUserName && tasksByDate) {
    try {
      const manager = new BigCalendarPlannerChatManager(
        notificationUserName,
        tasksByDate,
        handleCalendarUpdate,
        firestoreService
      );
      
      setChatManager(manager);
      console.log('✅ Chat manager initialized');
    } catch (error) {
      console.error('❌ Failed to initialize chat manager:', error);
    }
  }
}, [notificationUserName, tasksByDate, firestoreService]);

// Update chat manager when calendar changes
useEffect(() => {
  if (chatManager && tasksByDate) {
    chatManager.calendarData = tasksByDate;
  }
}, [tasksByDate, chatManager]);

// Update voice manager too

useEffect(() => {
  // Update voice manager when calendar changes
  if (voiceManager && tasksByDate) {
    voiceManager.updateCalendarData(tasksByDate);
  }
}, [tasksByDate, voiceManager]);

useEffect(() => {
  if (voiceManager && tasksByDate) {
    voiceManager.calendarData = tasksByDate;
  }
}, [tasksByDate, voiceManager]);

useEffect(() => {
  if (chatManager && tasksByDate) {
    chatManager.calendarData = tasksByDate;
  }
}, [tasksByDate, chatManager]);

// Initialize voice mode
const initializeVoiceMode = async () => {
  try {
    console.log('🎙️ Initializing calendar voice mode');
    
    setIsInitializingVoice(true);
    setVoiceStatus('Starting call...');
    
    // ✅ Create voice manager with CORRECT parameters (order matters!)
    const manager = new BigCalendarPlannerVoiceManager(
      "Host 1",                    // selectedVoice
      handleVoiceTranscript,       // onTranscriptUpdate
      setWaveformData,             // onWaveformUpdate
      setVoiceStatus,              // onStatusUpdate
      notificationUserName,        // userName
      tasksByDate,                 // calendarData
      handleCalendarUpdate,        // onCalendarUpdate
      notificationPermission,      // notificationPerm
      scheduleEventNotification,   // scheduleNotif
      firestoreService             // firestoreService
    );
    
    // ✅ Initialize manager (plays start sound inside)
    await manager.initialize();
    setVoiceManager(manager);
    
    // ✅ Start listening
    await manager.startListening();
    
    setIsVoiceMode(true);
    setIsInitializingVoice(false);
    setVoiceStatus('');
    
    // ✅ Random greeting
    const greetings = [
      `Hello${notificationUserName ? ` ${notificationUserName}` : ''}, what would you like to schedule?`,
      `Hi${notificationUserName ? ` ${notificationUserName}` : ''}, what can I schedule for you today?`,
      `Hello${notificationUserName ? ` ${notificationUserName}` : ''}, would you like to schedule something?`,
      `Hi${notificationUserName ? ` ${notificationUserName}` : ''}, what would you like to add to your calendar?`,
      `Hello${notificationUserName ? ` ${notificationUserName}` : ''}, how can I help you today?`,
      `Hi${notificationUserName ? ` ${notificationUserName}` : ''}, what can I assist you with?`,
    ];
    
    const greeting = greetings[Math.floor(Math.random() * greetings.length)];
    
    const greetingText = greeting.endsWith('need') || greeting.endsWith('plan') || greeting.endsWith('help')
      ? greeting + '?' 
      : greeting;
    
    // ✅ Show greeting in chat
    setVoiceMessages([{
      id: Date.now(),
      type: 'bot',
      content: greetingText,
      timestamp: new Date().toISOString()
    }]);
    
    // ✅ Play greeting audio
    await manager.streamAudioChunks(greetingText, "Host 1");
    
    console.log('✅ Calendar voice mode started');
    
  } catch (error) {
    console.error('❌ Voice mode error:', error);
    
    setIsInitializingVoice(false);
    setVoiceStatus('');
    
    // ✅ Cleanup on error
    if (voiceManager) {
      try {
        voiceManager.disconnect();
      } catch (cleanupError) {
        console.error('❌ Cleanup error:', cleanupError);
      }
      setVoiceManager(null);
    }
    
    setIsVoiceMode(false);
    
    // ✅ Show error in chat
    setVoiceMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: `Failed to start voice mode: ${error.message}`,
      timestamp: new Date().toISOString()
    }]);
  }
};
const handleCalendarUpdate = async (updatedCalendar) => {
  console.log('📅 Calendar update requested:', Object.keys(updatedCalendar).length, 'dates');
  
  try {
    // ✅ STEP 1: Update React state FIRST (this updates the UI IMMEDIATELY)
    setManualTasks(updatedCalendar);
    console.log('✅ State updated - UI will re-render NOW');

    // ✅ STEP 2: Update voice manager IMMEDIATELY
    if (voiceManager) {
      voiceManager.calendarData = updatedCalendar;
      console.log('✅ Voice manager calendar synced');
    }

    // ✅ STEP 3: Update chat manager IMMEDIATELY
    if (chatManager) {
      chatManager.calendarData = updatedCalendar;
      console.log('✅ Chat manager calendar synced');
    }

    // ✅ STEP 4: Save to Firestore (async, doesn't block UI)
    if (firestoreService?.isReady?.()) {
      saveCalendarToStorage(updatedCalendar, firestoreService)
        .then(() => console.log('💾 Calendar saved to Firestore'))
        .catch(err => console.error('❌ Firestore save failed:', err));
    }

  } catch (error) {
    console.error('❌ Calendar update failed:', error);
  }
};
// Effect to sync calendar data to managers
useEffect(() => {
  // Update chat manager when calendar changes
  if (chatManager && tasksByDate) {
    chatManager.updateCalendarData(tasksByDate);
  }
}, [tasksByDate, chatManager]);

const parseRelativeDateForVoice = (dateString) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const lower = dateString.toLowerCase();
  
  if (lower === 'today') return today;
  
  if (lower === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  }
  
  const nextDayMatch = lower.match(/next (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/);
  if (nextDayMatch) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDay = days.indexOf(nextDayMatch[1]);
    const currentDay = today.getDay();
    let daysToAdd = targetDay - currentDay;
    if (daysToAdd <= 0) daysToAdd += 7;
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysToAdd);
    return nextDate;
  }
  
  const parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) return parsed;
  
  return today;
};

// Handle voice transcripts
const handleVoiceTranscript = (transcript) => {
  console.log('📝 Voice transcript:', transcript.type, transcript.text);
  
  if (transcript.type === 'user') {
    setVoiceMessages(prev => {
      const lastMsg = prev[prev.length - 1];
      
      if (lastMsg?.isVoiceTyping && lastMsg?.type === 'user') {
        return prev.map((msg, idx) => 
          idx === prev.length - 1 
            ? { ...msg, content: transcript.text, isFinal: transcript.isFinal }
            : msg
        );
      } else if (transcript.text.trim()) {
        return [...prev, {
          id: Date.now(),
          type: 'user',
          content: transcript.text,
          isVoiceTyping: !transcript.isFinal,
          timestamp: new Date().toISOString()
        }].slice(-6);
      }
      return prev;
    });
  } 
  else if (transcript.type === 'assistant') {
    if (transcript.isLoading) {
      setVoiceMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        
        if (lastMsg?.isVoiceSpeaking && lastMsg?.type === 'bot') {
          return prev.map((msg, idx) =>
            idx === prev.length - 1
              ? { ...msg, content: '...', isStreaming: true }
              : msg
          );
        } else {
          return [...prev, {
            id: Date.now(),
            type: 'bot',
            content: '...',
            isVoiceSpeaking: true,
            isStreaming: true,
            timestamp: new Date().toISOString()
          }].slice(-6);
        }
      });
      return;
    }
    
    setVoiceMessages(prev => {
      const lastMsg = prev[prev.length - 1];
      
      if (lastMsg?.isVoiceSpeaking && lastMsg?.type === 'bot') {
        return prev.map((msg, idx) =>
          idx === prev.length - 1
            ? { ...msg, content: transcript.text, isFinal: transcript.isFinal, isStreaming: !transcript.isFinal }
            : msg
        );
      } else if (transcript.text.trim()) {
        return [...prev, {
          id: Date.now(),
          type: 'bot',
          content: transcript.text,
          isVoiceSpeaking: !transcript.isFinal,
          isStreaming: !transcript.isFinal,
          timestamp: new Date().toISOString()
        }].slice(-6);
      }
      return prev;
    });
  }
};
const handleVoiceScheduleEvent = async (args, toolCallId) => {
  console.log('📅 Voice schedule event:', args);
  
  try {
    const eventDate = parseRelativeDateForVoice(args.date);
    const dateKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
    
    const newEvent = {
      text: args.title,
      time: args.time || 'All day',
      description: args.description || '',
      completed: false,
      notifyEnabled: true,
      notificationId: null,
      createdAt: new Date().toISOString()
    };
    
    // Get current index before updating
    const currentIndex = (tasksByDate[dateKey] || []).length;
    
    // Schedule notification if enabled
    let notifId = null;
    if (notificationPermission) {
      notifId = await scheduleEventNotification(
        newEvent,
        dateKey,
        currentIndex,
        notificationUserName
      );
      newEvent.notificationId = notifId;
    }
    
    // Update state
    const updatedTasks = {
      ...manualTasks,
      [dateKey]: [...(manualTasks[dateKey] || []), newEvent]
    };
    
    setManualTasks(updatedTasks);
    
    // ✅ FIX: Update voice manager's calendar data immediately
    if (voiceManager) {
      voiceManager.calendarData = updatedTasks;
      console.log('📅 Voice manager calendar updated');
    }
    
    // Save to Firestore
    if (firestoreService?.isReady?.()) {
      await saveCalendarToStorage(updatedTasks, firestoreService);
      console.log('💾 Event saved to Firestore');
    }
    
    console.log('✅ Event scheduled successfully');
    
  } catch (error) {
    console.error('❌ Voice schedule error:', error);
    
    if (voiceManager) {
      await voiceManager.streamAudioChunks("Sorry, I couldn't schedule that event", "Host 1");
    }
  }
};
const getEventsForDate = (date) => {
  const dateKey = formatDateKey(date);
  const events = tasksByDate[dateKey] || [];
  
  // ✅ ADD THIS DEBUG LOG
  if (events.some(e => e.isRecurring)) {
    console.log(`📅 ${dateKey}: Found ${events.filter(e => e.isRecurring).length} recurring events`);
  }
  
  return events
    .map((task, index) => ({ 
      ...task, 
      id: `${dateKey}-${index}`, 
      dateKey, 
      index,
      isRecurringInstance: task.isRecurring || false,
      recurrencePattern: task.recurrencePattern || null
    }))
    .sort((a, b) => {
      const aMin = parseTimeToMinutes(a.time) || Infinity;
      const bMin = parseTimeToMinutes(b.time) || Infinity;
      return aMin - bMin;
    });
};

const handleVoiceButtonClick = async () => {
  if (isVoiceMode) {
    // ✅ CLEAR PENDING MESSAGE TIMEOUT
    if (messageTimeoutId) {
      console.log('🧹 Clearing message timeout...');
      clearTimeout(messageTimeoutId);
      setMessageTimeoutId(null);
    }
    
    // End voice call
    await stopVoiceMode();
  } else if (!isInitializingVoice) {
    // Start voice call
    await initializeVoiceMode();
  }
};



// Stop voice mode

const stopVoiceMode = async () => {
  try {
    console.log('🛑 Stopping calendar voice mode...');
    setVoiceStatus('Ending call...');
    
    if (voiceManager) {
      // ✅ CRITICAL: Stop audio FIRST (before anything else)
      console.log('🔊 Stopping all audio immediately...');
      voiceManager.interruptCurrentAudio();
      voiceManager.audioQueue = [];
      voiceManager.isPlayingAudio = false;
      voiceManager.isAISpeaking = false;
      console.log('✅ All audio stopped');
      
      // ✅ STEP 1: Stop listening
      voiceManager.stopListening();
      console.log('✅ Listening stopped');
      
      // ✅ STEP 2: Clear all timers
      if (voiceManager.silenceTimer) {
        clearTimeout(voiceManager.silenceTimer);
        voiceManager.silenceTimer = null;
      }
      console.log('✅ Timers cleared');
      
      // ✅ STEP 3: Disconnect
      voiceManager.disconnect();
      setVoiceManager(null);
      console.log('✅ Voice manager disconnected');
    }
    
    // ✅ STEP 4: Clear message timeout
    if (messageTimeoutId) {
      clearTimeout(messageTimeoutId);
      setMessageTimeoutId(null);
    }
    console.log('✅ Message timeout cleared');
    
    // ✅ STEP 5: Reset all states
    setIsVoiceMode(false);
    setIsInitializingVoice(false);
    setVoiceStatus('');
    setMicMuted(false);
    setWaveformData({
      points: '',
      isActive: false,
      height: 0
    });
    console.log('✅ States reset');
    
    // ✅ STEP 6: Clear messages after delay
    setTimeout(() => {
      setVoiceMessages([]);
      setInputValue('');
    }, 300);
    
    console.log('✅ Calendar voice mode ended cleanly');
    
  } catch (error) {
    console.error('❌ Error stopping voice mode:', error);
    
    // ✅ FORCE CLEANUP - Even if error occurred
    try {
      if (voiceManager) {
        // Stop audio IMMEDIATELY
        voiceManager.interruptCurrentAudio();
        voiceManager.audioQueue = [];
        voiceManager.isPlayingAudio = false;
        voiceManager.isAISpeaking = false;
        
        // Stop listening
        voiceManager.stopListening();
        
        // Clear timers
        if (voiceManager.silenceTimer) {
          clearTimeout(voiceManager.silenceTimer);
          voiceManager.silenceTimer = null;
        }
        
        // Disconnect
        voiceManager.disconnect();
        setVoiceManager(null);
      }
    } catch (cleanupError) {
      console.error('❌ Cleanup error:', cleanupError);
    }
    
    // ✅ RESET STATES ANYWAY
    setIsVoiceMode(false);
    setIsInitializingVoice(false);
    setVoiceStatus('');
    setMicMuted(false);
    setWaveformData({
      points: '',
      isActive: false,
      height: 0
    });
    
    if (messageTimeoutId) {
      clearTimeout(messageTimeoutId);
      setMessageTimeoutId(null);
    }
    
    setTimeout(() => {
      setVoiceMessages([]);
      setInputValue('');
    }, 300);
    
    console.log('✅ Force cleanup complete');
  }
};


useEffect(() => {
  return () => {
    if (chatManager) {
      chatManager.clearHistory();
    }
    if (isVoiceMode && voiceManager) {
      voiceManager.disconnect();
    }
  };
}, [chatManager, isVoiceMode, voiceManager]);

const handleSendMessage = async () => {
  if (!inputValue.trim() || !chatManager) return;
  
  const userMessage = inputValue.trim();
  setInputValue('');
  setIsSendingMessage(true);
  
  // Clear existing timeout
  if (messageTimeoutId) {
    clearTimeout(messageTimeoutId);
    setMessageTimeoutId(null);
  }
  
  const newUserMsg = {
    id: Date.now(),
    type: 'user',
    content: userMessage,
    timestamp: new Date().toISOString()
  };
  
  setVoiceMessages(prev => [...prev, newUserMsg].slice(-10));
  
  // Add loading message
  const loadingMsg = {
    id: Date.now() + 1,
    type: 'bot',
    content: '...',
    isLoading: true,
    timestamp: new Date().toISOString()
  };
  
  setVoiceMessages(prev => [...prev, loadingMsg].slice(-10));
  
  try {
    // ✅ FIX: Wait for AI response
    const aiResponse = await chatManager.sendMessage(userMessage);
    
    // Remove loading message and add real response
    setVoiceMessages(prev => {
      const withoutLoading = prev.filter(msg => !msg.isLoading);
      return [...withoutLoading, {
        id: Date.now() + 2,
        type: 'bot',
        content: aiResponse,
        timestamp: new Date().toISOString()
      }].slice(-10);
    });
    
    // Start 1 min timer to clear messages after inactivity
    const timeoutId = setTimeout(() => {
      setVoiceMessages([]);
      setMessageTimeoutId(null);
    }, 60000);
    
    setMessageTimeoutId(timeoutId);
    
  } catch (error) {
    console.error('❌ Error:', error);
    
    // Remove loading and add error
    setVoiceMessages(prev => {
      const withoutLoading = prev.filter(msg => !msg.isLoading);
      return [...withoutLoading, {
        id: Date.now() + 2,
        type: 'bot',
        content: 'Had trouble with that',
        timestamp: new Date().toISOString()
      }].slice(-10);
    });
    
    // Clear error message after 5 seconds
    const timeoutId = setTimeout(() => {
      setVoiceMessages([]);
      setMessageTimeoutId(null);
    }, 5000);
    
    setMessageTimeoutId(timeoutId);
  } finally {
    setIsSendingMessage(false);
  }
};

// ============================================
// HELPER: Highlight time in text with blue color
// ============================================
const highlightTimeInText = (text) => {
  // Regex to match times: 4pm, 4:30pm, 4:30 pm, 16:00, etc.
  const timeRegex = /(\d{1,2}):?(\d{2})?\s*(am|pm)?/gi;
  
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Reset regex
  timeRegex.lastIndex = 0;
  
  while ((match = timeRegex.exec(text)) !== null) {
    // Add text before the time
    if (match.index > lastIndex) {
      parts.push({
        text: text.slice(lastIndex, match.index),
        isTime: false
      });
    }
    
    // Add the time (highlighted)
    parts.push({
      text: match[0],
      isTime: true
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      text: text.slice(lastIndex),
      isTime: false
    });
  }
  
  return parts.length > 0 ? parts : [{ text, isTime: false }];
};

  const [newEvent, setNewEvent] = useState({ 
    title: '', 
    time: '', 
    description: '',
    notifyEnabled: true
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedFromCell, setDraggedFromCell] = useState(null);
  
  const scrollContainerRef = useRef(null);
useEffect(() => {
  let isMounted = true;
  let retryCount = 0;
  const maxRetries = 5;
  const retryDelayMs = 500;

  const loadUserCalendar = async () => {
    if (!isMounted) return;

    const service = firestoreService || window.firestoreService;
    
    if (!service) {
      console.log(`⏳ Waiting for Firestore service... (attempt ${retryCount + 1}/${maxRetries})`);
      
      if (retryCount < maxRetries) {
        retryCount++;
        const delay = retryDelayMs * Math.pow(2, retryCount - 1);
        setTimeout(loadUserCalendar, delay);
      } else {
        console.error('❌ Firestore service never became available');
        setIsLoading(false);
      }
      return;
    }

    const isServiceReady = typeof service.isReady === 'function' 
      ? service.isReady() 
      : service.isReady;

    if (!isServiceReady) {
      console.log(`⏳ Firestore not ready yet... (attempt ${retryCount + 1}/${maxRetries})`);
      
      if (retryCount < maxRetries) {
        retryCount++;
        const delay = retryDelayMs * Math.pow(2, retryCount - 1);
        setTimeout(loadUserCalendar, delay);
      } else {
        console.error('❌ Firestore service never became ready');
        setIsLoading(false);
      }
      return;
    }

    console.log('✅ Firestore ready - loading calendar...');
    setIsLoading(true);
    
    try {
      // ✅ CRITICAL: Force fresh load from Firestore
      const calendar = await loadCalendarFromStorage(service);
      
      if (isMounted) {
        console.log('📅 Calendar loaded:', {
          dates: Object.keys(calendar).length,
          totalEvents: Object.values(calendar).reduce((sum, events) => sum + events.length, 0),
          sample: Object.keys(calendar).slice(0, 3)
        });
        
        setManualTasks(calendar);
      }
    } catch (error) {
      console.error('❌ Failed to load calendar:', error);
      if (isMounted) {
        setManualTasks({});
      }
    } finally {
      if (isMounted) {
        setIsLoading(false);
      }
    }
  };

  loadUserCalendar();

  return () => {
    isMounted = false;
  };
}, [firestoreService]); // ✅ Re-run when firestoreService changes

// ✅ ADD: Auto-refresh when returning to calendar tab
useEffect(() => {
  const handleVisibilityChange = async () => {
    if (!document.hidden && firestoreService?.isReady?.()) {
      console.log('👀 Tab visible - refreshing calendar...');
      
      try {
        const calendar = await loadCalendarFromStorage(firestoreService);
        setManualTasks(calendar);
        console.log('✅ Calendar refreshed');
      } catch (error) {
        console.warn('⚠️ Failed to refresh calendar:', error);
      }
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [firestoreService]);
// ============================================
// ALSO UPDATE: Save calendar useEffect
// Make it more robust
// ============================================

useEffect(() => {
  // Don't save if loading or service not ready
  if (isLoading || !firestoreService?.isReady?.()) {
    return;
  }

  // Debounce saves - wait 1 second after last change
  const saveTimer = setTimeout(async () => {
    try {
      console.log('💾 Auto-saving calendar...');
      await saveCalendarToStorage(manualTasks, firestoreService);
      console.log('✅ Calendar saved successfully');
    } catch (error) {
      console.error('⚠️ Failed to auto-save calendar:', error);
      // Don't crash - user data is still in state
    }
  }, 1000);

  // Clean up timer if component unmounts or deps change
  return () => clearTimeout(saveTimer);
}, [manualTasks, firestoreService, isLoading]);

// ============================================
// ADD: Effect to listen for service changes
// ============================================

useEffect(() => {
  if (!firestoreService) {
    console.warn('⚠️ Firestore service not available');
    return;
  }

  console.log('✅ BigCalendarPlanner received firestoreService:', {
    isReady: firestoreService.isReady?.(),
    hasGetUserData: typeof firestoreService.getUserData === 'function'
  });

  // Try to load calendar when service becomes ready
  const checkService = setInterval(() => {
    if (firestoreService?.isReady?.()) {
      console.log('✅ Service became ready, loading calendar...');
      clearInterval(checkService);
      // Trigger reload by updating loading state
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 100);
    }
  }, 500);

  return () => clearInterval(checkService);
}, [firestoreService]);

const handleAddEvent = async () => {
  if (!newEvent.title.trim()) {
    alert('Please enter an event title');
    return;
  }

  const targetDate = selectedTimeSlot?.date || new Date();
  const dateKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;
  
  const eventData = {
    text: newEvent.title,
    time: newEvent.time || 'All day',
    description: newEvent.description,
    completed: false,
    notifyEnabled: true,
    notificationId: null,
    createdAt: new Date().toISOString()
  };

  try {
    const currentIndex = (tasksByDate[dateKey] || []).length;
    
    if (notificationPermission) {
      const notifId = await scheduleEventNotification(
        eventData, 
        dateKey, 
        currentIndex,
        notificationUserName  
      );
      eventData.notificationId = notifId;
    }
    
    setManualTasks(prev => ({
      ...prev,
      [dateKey]: [...(prev[dateKey] || []), eventData]
    }));
    
    // ✅ TRIGGER ANIMATION
    triggerEventAnimation(dateKey, currentIndex, 'add');
    
    if (firestoreService?.isReady?.()) {
      console.log('📤 Auto-syncing event to Firestore...');
      const { autoSyncEventToCalendar } = require('./schedulerHandlers');
      const syncSuccess = await autoSyncEventToCalendar(
        eventData,
        dateKey,
        firestoreService
      );
      
      if (!syncSuccess) {
        console.warn('⚠️ Event saved locally but sync failed');
      }
    }
    
    setShowAddModal(false);
    setNewEvent({ title: '', time: '', description: '', notifyEnabled: true });
    setSelectedTimeSlot(null);
    
  } catch (error) {
    console.error('❌ Error adding event:', error);
    alert('Failed to add event. Please try again.');
  }
};




// ==============
useEffect(() => {
  // Define async function inside useEffect
  const loadUserName = async () => {
    try {
      const service = firestoreService || window.firestoreService;
      const name = await getStoredUserName(service);
      setNotificationUserName(name || 'there');
    } catch (error) {
      console.error('Failed to load username for notifications:', error);
      setNotificationUserName('there');
    }
  };
  
  // Call the async function
  loadUserName();
}, [firestoreService]);
  // Handler function
  const handleSyncComplete = (events, source) => {
    setExternalEvents(prev => {
      const filtered = prev.filter(e => e.source !== source);
      const updated = [...filtered, ...events];
      localStorage.setItem('external_calendar_events', JSON.stringify(updated));
      return updated;
    });
  };

useEffect(() => {
  return () => {
    if (messageTimeoutId) {
      clearTimeout(messageTimeoutId);
    }
    
    if (chatManager) {
      chatManager.clearHistory();
    }
    
    if (isVoiceMode && voiceManager) {
      voiceManager.disconnect();
    }
  };
}, [chatManager, isVoiceMode, voiceManager, messageTimeoutId]);

// ✅ ADD THIS ENTIRE useEffect - Clear timeout when user starts typing
useEffect(() => {
  if (inputValue && messageTimeoutId) {
    clearTimeout(messageTimeoutId);
    setMessageTimeoutId(null);
  }
}, [inputValue, messageTimeoutId]);
  // ✅ Save calendar when it changes (debounced)
  useEffect(() => {
    if (!firestoreService?.isReady() || isLoading) return;

    const saveTimer = setTimeout(async () => {
      await saveCalendarToStorage(manualTasks, firestoreService);
    }, 1000); // Save 1 second after last change

    return () => clearTimeout(saveTimer);
  }, [manualTasks, firestoreService, isLoading]);

  useEffect(() => {
    const checkPermission = async () => {
      const granted = await requestNotificationPermission();
      setNotificationPermission(granted);
      
      if (granted && Capacitor.isNativePlatform()) {
        LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
          console.log('Notification tapped:', notification);
        });
      }
    };
    
    checkPermission();
  }, []);
useEffect(() => {
  // Handle OAuth callback
  handleOAuthCallback();
  
  // Load stored external events
  const stored = localStorage.getItem('external_calendar_events');
  if (stored) {
    try {
      setExternalEvents(JSON.parse(stored));
    } catch (e) {
      console.error('Failed to load external events:', e);
    }
  }
}, []);
  const colors = [
    { bg: '#e9e7fcff', border: '#a8bbf9ff', text: '#182883ff' },
    { bg: '#dbeafe', border: '#93c5fd', text: '#1e3a8a' },
    { bg: '#d1fae5', border: '#86efac', text: '#065f46' },
    { bg: '#fef3c7', border: '#fcd34d', text: '#78350f' },
    { bg: '#e9d5ff', border: '#c084fc', text: '#581c87' },
    { bg: '#ffedd5', border: '#fdba74', text: '#7c2d12' }
  ];

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const weekDays = useMemo(() => {
  const days = [];
  const curr = new Date(currentDate);
  curr.setHours(0, 0, 0, 0);
  
  const currentDay = curr.getDay();
  const firstDayOfWeek = new Date(curr);
  firstDayOfWeek.setDate(curr.getDate() - currentDay);
  
  for (let i = 0; i < 7; i++) {
    const day = new Date(firstDayOfWeek);
    day.setDate(firstDayOfWeek.getDate() + i);
    days.push(day);
  }
  
  console.log('📅 Week days generated:');
  days.forEach((day, i) => {
    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i];
    console.log(`   ${dayName}: ${formatDateKey(day)} (${day.toDateString()})`);
  });
  
  return days;
}, [currentDate]);
const todayEvents = useMemo(() => {
  const today = new Date();
  // ✅ NEW: Use YYYY-MM-DD format
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  return (tasksByDate[todayKey] || [])
    .map((task, index) => ({ ...task, id: `${todayKey}-${index}`, dateKey: todayKey, index }))
    .sort((a, b) => {
      const aMin = parseTimeToMinutes(a.time) || Infinity;
      const bMin = parseTimeToMinutes(b.time) || Infinity;
      return aMin - bMin;
    });
}, [tasksByDate]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      const targetHour = Math.max(0, currentHour - 2);
      const scrollPosition = targetHour * 60;
      scrollContainerRef.current.scrollTop = scrollPosition;
    }
  }, []);

  useEffect(() => {
    const updateTimeLinePosition = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const positionFromTop = (hours * 60) + (minutes);
      
      const timeLines = document.querySelectorAll('.current-time-line');
      timeLines.forEach(line => {
        if (line.parentElement?.classList.contains('today-col')) {
          line.style.transform = `translateY(${positionFromTop}px)`;
        }
      });
    };

    updateTimeLinePosition();
    const interval = setInterval(updateTimeLinePosition, 60000);
    return () => clearInterval(interval);
  }, []);

const handleDeleteEvent = async () => {
  if (!selectedEvent) return;
  
  const { dateKey, index } = selectedEvent;
  
  // ✅ TRIGGER DELETE ANIMATION FIRST
  triggerEventAnimation(dateKey, index, 'delete');
  
  // Wait for animation to complete
  setTimeout(async () => {
    const updated = await deleteEvent(manualTasks, dateKey, index, firestoreService);
    
    setManualTasks(updated);
    setShowEventModal(false);
    setSelectedEvent(null);
  }, 500);
};
  const handleToggleCompletion = () => {
    if (!selectedEvent) return;
    const { dateKey, index } = selectedEvent;
    
    setManualTasks(prev => ({
      ...prev,
      [dateKey]: prev[dateKey].map((task, idx) => 
        idx === index ? { ...task, completed: !task.completed } : task
      )
    }));
    
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleToggleTodayEvent = async (dateKey, index) => {
    const updated = await updateEventCompletion(
      manualTasks,
      dateKey,
      index,
      !manualTasks[dateKey][index].completed,
      firestoreService
    );
    setManualTasks(updated);
  };


  // NEW: Handle drag start
  const handleDragStart = (e, event, fromDate, fromHour) => {
    e.stopPropagation();
    setIsDragging(true);
    setDraggedEvent(event);
    setDraggedFromCell({ date: fromDate, hour: fromHour });
    e.dataTransfer.effectAllowed = 'move';
  };

  // NEW: Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // NEW: Handle drop
const handleDrop = async (e, toDate, toHour) => {
  e.preventDefault();
  e.stopPropagation();
  
  if (!draggedEvent || !draggedFromCell) return;

  // ✅ FIX: Use formatDateKey for consistent date formatting
  const fromDateKey = draggedEvent.dateKey;
  const toDateKey = formatDateKey(toDate); // Use formatDateKey instead of getDate()
  
  const { index, notificationId } = draggedEvent;

  // Calculate new time
  const ampm = toHour >= 12 ? 'PM' : 'AM';
  const displayHour = toHour % 12 || 12;
  const newTime = `${displayHour}:00 ${ampm}`;

  // Cancel old notification
  if (notificationId) {
    await cancelEventNotification(notificationId);
  }

  // Create updated event
  const updatedEvent = { ...draggedEvent, time: newTime };

  // Schedule new notification if enabled
  let newNotificationId = null;
  if (draggedEvent.notifyEnabled && notificationPermission) {
    newNotificationId = await scheduleEventNotification(
      updatedEvent, 
      toDateKey, 
      tasksByDate[toDateKey]?.length || 0,
      notificationUserName  
    );
  }

  updatedEvent.notificationId = newNotificationId;

  // Update state
  setManualTasks(prev => {
    const updated = { ...prev };
    
    // Remove from old location
    updated[fromDateKey] = prev[fromDateKey].filter((_, idx) => idx !== index);
    if (updated[fromDateKey].length === 0) delete updated[fromDateKey];
    
    // Add to new location
    updated[toDateKey] = [...(updated[toDateKey] || []), updatedEvent];
    
    return updated;
  });

  setIsDragging(false);
  setDraggedEvent(null);
  setDraggedFromCell(null);
};

  const startDragEvent = (event) => {
    setDraggedEvent(event);
    setShowDragModal(true);
  };

  const handleDragTimeChange = async (minutes) => {
    if (!draggedEvent) return;
    const newTime = minutesToTimeString(minutes);
    const { dateKey, index, notificationId } = draggedEvent;
    
    if (notificationId) {
      await cancelEventNotification(notificationId);
    }
    
    const updatedEvent = { ...draggedEvent, time: newTime };
    
    let newNotificationId = null;
    if (draggedEvent.notifyEnabled && notificationPermission) {
      newNotificationId = await scheduleEventNotification(updatedEvent, dateKey, index, notificationUserName);
    }
    
    setManualTasks(prev => ({
      ...prev,
      [dateKey]: prev[dateKey].map((task, idx) => 
        idx === index ? { ...task, time: newTime, notificationId: newNotificationId } : task
      )
    }));
    
    setDraggedEvent(prev => ({ ...prev, time: newTime, notificationId: newNotificationId }));
  };

  const handleCellClick = (date, hour) => {
    if (isDragging) return; // Don't open modal when dragging
    
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const timeStr = `${displayHour}:00 ${ampm}`;
    
    setSelectedTimeSlot({ date, hour, time: timeStr });
    setNewEvent({ title: '', time: timeStr, description: '', notifyEnabled: true });
    setShowAddModal(true);
  };


  const hours = Array.from({ length: 24 }, (_, i) => {
    const ampm = i >= 12 ? 'PM' : 'AM';
    const displayHour = i % 12 || 12;
    return `${displayHour}:00 ${ampm}`;
  });

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
        }
        .notification-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px;
          background: rgba(0, 0, 0, 0.03);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .notification-toggle:hover {
          background: rgba(0, 0, 0, 0.05);
        }
        
        .notification-toggle-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
        }
        
        .notification-switch {
          position: relative;
          width: 44px;
          height: 24px;
          background: #e5e7eb;
          border-radius: 12px;
          transition: all 0.3s ease;
        }
        
        .notification-switch.enabled {
          background: #10b981;
        }
        
        .notification-switch-thumb {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 10px;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .notification-switch.enabled .notification-switch-thumb {
          transform: translateX(20px);
        }

        .notification-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: #d1fae5;
          color: #065f46;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 600;
        }
        .calendar-container {
          height: 100vh;
          background: linear-gradient(170deg, #e3e3e3d8 0%, #fefefecf 60%, #e0e7fcb2 100%);
          padding: 16px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          padding-top: 80px;
          overflow-y:auto;
        }

        .calendar-inner {
          max-width: 780px;
          margin: 0 auto;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .header h1 {
          font-size: 16px;
          font-weight: 500;
          color: #1f2937;
          margin: 0;
        }

        .add-event-button {
          background: #272727;
          border: none;
          color: white;
          padding: 10px 16px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .add-event-button:hover {
          background: #3f3f3f;
          transform: translateY(-1px);
        }

        .nav-container {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .nav-button {
          background: rgba(255, 255, 255, 1);
          border: none;
          color: #374151;
          padding: 8px;
          border-radius: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
                   border: 1px solid rgba(201, 201, 201, 0.93);
          transition: all 0.2s ease;
        }

        .nav-button:hover {
          background: rgba(255, 255, 255, 1);
                    color: #9a9a9aff;
        }

        .date-range {
          color: #1f2937;
          font-size: 14px;
          font-weight: 600;
          margin: 0;
          min-width: 150px;
        }

        .today-button {
          background: #272727;
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .today-button:hover {
          background: #3f3f3f;
        }

        .glass-container {
          background: rgba(255, 255, 255, 0.31);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 7px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.93);
          overflow: hidden;
          margin-bottom: 20px;
        }

        .calendar-content {
          background: white;
          border-radius: 14px;
          overflow: hidden;
         border: 1px solid rgba(213, 213, 213, 0.93);
        }

        .day-headers {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          background: rgba(0, 0, 0, 0.02);
        }

        .time-header {
          padding: 12px 8px;
          text-align: center;
          font-size: 10px;
          font-weight: 600;
          color: #6b7280;
        }

        .day-header {
          padding: 12px 8px;
          text-align: center;
          border-right: 1px solid rgba(0, 0, 0, 0.06);
        }

        .day-header:last-child {
          border-right: none;
        }

        .day-header.today {
          background: rgba(102, 126, 234, 0.05);
        }

        .day-name {
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 4px;
        }

        .day-number {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .day-number.today-num {
          color: #667eea;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          max-height: 300px;
          overflow: auto;
        }

        .time-column {
          border-right: 1px solid rgba(0, 0, 0, 0.06);
          background: rgba(0, 0, 0, 0.02);
        }

        .hour-cell {
          padding: 12px 4px;
          text-align: center;
          font-size: 9px;
          font-weight: 500;
          color: #9ca3af;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          min-height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .event-column {
          border-right: 1px solid rgba(0, 0, 0, 0.06);
        }

        .event-column:last-child {
          border-right: none;
        }

        .event-column.today-col {
          background: rgba(102, 126, 234, 0.02);
        }

        .event-cell {
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          min-height: 60px;
          padding: 4px;
          position: relative;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .event-cell:hover {
          background-color: rgba(102, 126, 234, 0.05);
        }

        .current-time-line {
          position: absolute;
          height: 2px;
          background: linear-gradient(90deg, #3b82f6, #60a5fa);
          box-shadow: 0 0 8px rgba(59, 130, 246, 0.6);
          left: 0;
          right: 0;
          z-index: 50;
        }

        .current-time-indicator {
          position: absolute;
          width: 10px;
          height: 10px;
          background: #3b82f6;
          border: 2px solid white;
          border-radius: 50%;
          left: -7px;
          top: -4px;
        }

        .event-item {
          padding: 4px 6px;
          border-radius: 6px;
          font-size: 9px;
          font-weight: 500;
          margin-bottom: 2px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid;
        }

        .event-item:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .event-item.completed {
          background: #f3f4f6 !important;
          border: 1px solid #e5e7eb !important;
          color: #6b7280;
          text-decoration: line-through;
          opacity: 0.6;
        }

        .agenda-section {
          margin-top: 0;
        }

        .agenda-header {
          padding: 20px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }

        .agenda-header-content {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .agenda-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: #e0e7ff;
          border-radius: 10px;
          flex-shrink: 0;
        }

        .agenda-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .date-badge {
          margin-left: auto;
          padding: 6px 12px;
          background: linear-gradient(135deg, #1f1f1fff);
          border-radius: 8px;
          font-size: 11px;
          font-weight: 500;
          color: white;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #9ca3af;
        }

        .empty-icon {
          margin: 0 auto 12px;
          opacity: 0.5;
        }

        .empty-text {
          margin: 0;
          font-size: 13px;
        }

.event-list {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 500px;
  overflow-y: auto;
}
 
@media (max-width: 768px) {
  .event-list {
    padding: 12px 14px;
    gap: 10px;
    max-height: 400px; /* ✅ INCREASE from 170px */
  }
}

@media (max-width: 480px) {
  .event-list {
    padding: 10px 12px;
    gap: 8px;
    max-height: 300px; /* ✅ INCREASE from 130px */
  }
}


        .event-card {
          background: rgba(255, 255, 255, 0.35);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          padding: 3px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.68);
          transition: transform 0.2s ease;
          cursor: pointer;
        }

        .event-card:hover {
          transform: translateY(-2px);
        }

        .event-card-inner {
          border-radius: 10px;
          padding: 6px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .event-card-inner.completed {
          background: #f9fafb;
          opacity: 0.7;
        }

        .check-button {
          background: white;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }

        .check-button:hover {
          transform: scale(1.05);
        }

        .event-details {
          flex: 1;
          min-width: 0;
        }

        .event-title {
        text-align:left;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 10px;
        }

        .event-title.completed {
          color: #6b7280;
          text-decoration: line-through;
        }

        .event-time {
          font-size: 11px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .event-time.completed {
          color: #9ca3af;
        }

        .event-description {
          font-size: 11px;
          margin-top: 0px;
        }

        .event-description.completed {
          color: #9ca3af;
        }

        .drag-button {
          background: white;
          border: none;
          cursor: grab;
          padding: 6px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
          color: #9ca3af;
        }

        .drag-button:hover {
          background: #f3f4f6;
          color: #6b7280;
        }

        .drag-button:active {
          cursor: grabbing;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .modal {
          background: white;
          border-radius: 20px;
          padding: 24px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.06);
        }

        .drag-modal {
          max-height: 70vh;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 20px;
          gap: 12px;
        }

        .modal-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          word-break: break-word;
        }

        .close-button {
          background: rgba(0, 0, 0, 0.04);
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 8px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .close-button:hover {
          background: rgba(0, 0, 0, 0.08);
        }

        .modal-content {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 20px;
        }

        .info-box {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: rgba(0, 0, 0, 0.03);
          border-radius: 12px;
        }

        .info-label {
          font-size: 11px;
          color: #9ca3af;
          margin-bottom: 2px;
        }

        .info-value {
          font-size: 13px;
          font-weight: 500;
          color: #1f2937;
        }

        .info-text {
          font-size: 13px;
          color: #374151;
          line-height: 1.5;
        }

        .status-badge {
          display: inline-flex;
          padding: 8px 14px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
          align-items: center;
          gap: 6px;
        }

        .status-badge.completed {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.pending {
          background: #fef3c7;
          color: #78350f;
        }

        .modal-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .action-button {
          flex: 1;
          min-width: 120px;
          padding: 12px;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-button {
          background: #d1fae5;
          color: #065f46;
        }

        .toggle-button.incomplete {
          background: #fef3c7;
          color: #78350f;
        }

        .toggle-button:hover {
          opacity: 0.8;
        }

        .delete-button {
          padding: 12px 18px;
          background: #fee2e2;
          color: #dc2626;
          display: flex;
          align-items: center;
          gap: 6px;
          justify-content: center;
        }

        .delete-button:hover {
          background: #fecaca;
        }

        .time-picker-container {
          margin: 20px 0;
          max-height: 300px;
        }

        .time-slots {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          max-height: 280px;
          overflow-y: auto;
          padding: 8px 0;
        }
.time-slot {
          padding: 10px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          text-align: center;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
          color: #374151;
        }
        .time-slot:hover {
          border-color: #272727;
          background: #f9fafb;
        }

        .time-slot.selected {
          background: #272727;
          color: white;
          border-color: #272727;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.2s ease;
        }

        .form-input:focus {
          border-color: #272727;
          outline: none;
          box-shadow: 0 0 0 3px rgba(39, 39, 39, 0.1);
        }

        .form-textarea {
          min-height: 100px;
          resize: vertical;
        }

        .form-actions {
          display: flex;
          gap: 12px;
        }

        .cancel1-button {
          flex: 1;
          padding: 10px 16px;
          background: rgba(0, 0, 0, 0.04);
          color: #374151;
          border: none;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .cancel1-button:hover {
          background: rgba(0, 0, 0, 0.08);
        }

        .submit1-button {
          flex: 1;
          padding: 10px 16px;
          background: #272727;
          color: white;
          border: none;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .submit1-button:hover {
          background: #3f3f3f;
          transform: translateY(-1px);
        }

        .time-display {
          font-size: 12px;
          color: #6b7280;
          padding: 8px 0;
        }

 @media (max-width: 768px) {
          .calendar-container {
            padding: 10px;
            padding-top: 70px;
          }

          .header h1 {
            font-size: 16px;
          }

          .add-event-button {
            padding: 8px 12px;
            font-size: 10px;
            gap: 6px;
          }

          .nav-button {
            padding: 6px;
          }

          .date-range {
            font-size: 12px;
            min-width: 130px;
          }

          .today-button {
            padding: 6px 12px;
            font-size: 11px;
          }

          .glass-container {
            margin-bottom: 10px;
          }

          .day-headers {
            grid-template-columns: 55px repeat(7, 1fr);
          }

          .calendar-grid {
            grid-template-columns: 55px repeat(7, 1fr);
            max-height: 250px;
          }

          .time-header {
            font-size: 9px;
            padding: 10px 4px;
          }

          .day-header {
            padding: 10px 4px;
          }

          .day-name {
            font-size: 9px;
          }

          .day-number {
            font-size: 12px;
          }

          .hour-cell {
            font-size: 8px;
            padding: 10px 2px;
            min-height: 50px;
          }

          .event-cell {
            min-height: 50px;
            padding: 3px;
          }

          .event-item {
            font-size: 8px;
            padding: 3px 4px;
            margin-bottom: 1px;
          }

          .agenda-icon {
            width: 28px;
            height: 28px;
          }

          .agenda-title {
            font-size: 13px;
          }

          .date-badge {
            font-size: 9px;
            padding: 4px 8px;
          }

          .agenda-header {
            padding: 14px;
          }

          .event-list {
            padding: 12px 14px;
            gap: 10px;
          }

          .event-card-inner {
            gap: 10px;
            padding: 5px;
          }

          .check-button {
            padding: 3px;
          }

          .event-title {
            font-size: 12px;
          }

          .event-time {
            font-size: 10px;
            gap: 4px;
          }

          .event-description {
            font-size: 10px;
          }

          .drag-button {
            padding: 4px;
          }

          .modal {
            padding: 18px;
          }

          .modal-title {
            font-size: 15px;
          }

          .info-box {
            padding: 12px;
            gap: 10px;
          }

          .info-label {
            font-size: 10px;
          }

          .info-value {
            font-size: 12px;
          }

          .info-text {
            font-size: 12px;
          }

          .status-badge {
            padding: 6px 12px;
            font-size: 11px;
          }

          .action-button {
            padding: 10px;
            font-size: 12px;
            min-width: 100px;
          }

          .delete-button {
            padding: 10px 14px;
            font-size: 12px;
          }

          .time-slots {
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
            max-height: 240px;
          }

          .time-slot {
            padding: 8px;
            font-size: 11px;
          }

          .form-label {
            font-size: 11px;
          }

          .form-input {
            padding: 10px 12px;
            font-size: 13px;
          }

          .form-textarea {
            min-height: 80px;
          }

          .cancel1-button, .submit1-button {
            padding: 9px 14px;
            font-size: 13px;
          }

          .time-display {
            font-size: 11px;
          }
        }

        @media (max-width: 480px) {
          .calendar-container {
            padding: 8px;
            padding-top: 66px;
          }

          .header {
            gap: 8px;
            margin-bottom: 16px;
          }

          .header h1 {
            font-size: 15px;
          }

          .add-event-button {
            padding: 5.5px 10px;
            font-size: 10px;
          }

          .nav-container {
            gap: 6px;
            margin-bottom: 16px;
          }

          .nav-button {
            padding: 5px;
          }

          .date-range {
            font-size: 12px;
            min-width: 110px;
          }

          .today-button {
            padding: 5px 10px;
            font-size: 10px;
          }

          .day-headers {
            grid-template-columns: 48px repeat(7, 1fr);
          }

          .calendar-grid {
            grid-template-columns: 46px repeat(7, 1fr);
            max-height: 220px;
          }

          .time-header {
            font-size: 9px;
            padding: 8px 2px;
          }

          .day-header {
            padding: 8px 2px;
          }

          .day-name {
            font-size: 9px;
          }

          .day-number {
            font-size: 11px;
          }

          .hour-cell {
            font-size: 7px;
            padding: 8px 1px;
            min-height: 45px;
          }

          .event-cell {
            min-height: 45px;
            padding: 2px;
          }

          .event-item {
            font-size: 7px;
            padding: 2px 3px;
          }

          .agenda-icon {
            width: 24px;
            height: 24px;
          }

          .agenda-title {
            font-size: 12.5px;
          }

          .date-badge {
            font-size: 9px;
            padding: 4px 6px;
          }

          .agenda-header {
            padding: 12px;
          }

          .event-list {
            padding: 10px 12px;
            gap: 8px;
          }

          .event-card-inner {
            gap: 8px;
            padding: 4px;
          }

          .event-title {
            font-size: 11px;
          }

          .event-time {
            font-size: 11px;
          }

          .event-description {
            font-size: 11px;
          }

          .modal {
            padding: 16px;
          }

          .modal-header {
            margin-bottom: 16px;
          }

          .modal-title {
            font-size: 14px;
          }

          .modal-content {
            gap: 12px;
            margin-bottom: 16px;
          }

          .info-box {
            padding: 10px;
            gap: 8px;
          }

          .status-badge {
            padding: 5px 10px;
            font-size: 10px;
          }

          .modal-actions {
            flex-direction: column;
            gap: 8px;
          }

          .action-button {
            width: 100%;
            padding: 9px;
            font-size: 11px;
            min-width: auto;
          }

          .delete-button {
            width: 100%;
            padding: 9px 12px;
            font-size: 11px;
          }

          .time-picker-container {
            margin: 16px 0;
            max-height: 240px;
          }

          .time-slots {
            grid-template-columns: repeat(2, 1fr);
            gap: 5px;
            max-height: 200px;
          }

          .time-slot {
            padding: 7px;
            font-size: 10px;
          }

          .form-group {
            margin-bottom: 12px;
          }

          .form-label {
            font-size: 10px;
            margin-bottom: 6px;
          }

          .form-input {
            padding: 9px 10px;
            font-size: 12px;
          }

          .form-textarea {
            min-height: 70px;
          }

          .form-actions {
            gap: 8px;
          }

          .cancel1-button, .submit1-button {
            padding: 8px 12px;
            font-size: 12px;
          }

          .time-display {
            font-size: 10px;
            padding: 6px 0;
          }
        }
          
.external-event {
  position: relative;
  padding-left: 8px;
}

.external-event::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 2px;
}

.external-event.google::before {
  background: linear-gradient(180deg, #4285f4, #34a853);
}

.external-event.microsoft::before {
  background: linear-gradient(180deg, #0078d4, #00bcf2);
}

.external-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 8px;
  font-weight: 600;
  margin-left: 4px;
  text-transform: uppercase;
}

.external-badge.google {
  background: #e8f4fd;
  color: #1967d2;
}

.external-badge.microsoft {
  background: #e6f2ff;
  color: #0078d4;
}.event-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid white;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, var(--circle-color-start), var(--circle-color-end));
}

.event-circle:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.event-circle.completed {
  opacity: 0.5;
  filter: grayscale(50%);
}
  
.schedule-wrapper {
  margin-top: 0;
}

.schedule-container {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.schedule-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.schedule-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.schedule-header-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.schedule-date-label {
  padding: 6px 12px;
  background: #1118270a;
  color: white;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
    color: #8d9096ff;
}

.schedule-empty {
  text-align: center;
  padding: 60px 24px;
}

.schedule-empty-text {
  margin: 0;
  font-size: 14px;
  color: #9ca3af;
}

.schedule-list {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
}

.schedule-event {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 18px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-left: 3px solid;
}

.schedule-event.is-completed {
  background: #f9fafb;
  opacity: 0.5;
}

.schedule-event-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.schedule-event-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
  text-align: left;
  word-wrap: break-word;
  flex: 1;
}

.schedule-event-title.is-completed {
  color: #9ca3af;
  text-decoration: line-through;
}

.schedule-check-btn {
  width: 22px;
  height: 22px;
  border: 2px solid #d1d5db;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  flex-shrink: 0;
}

.schedule-check-btn.is-checked {
   background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: #1078b91e;
}

.schedule-event-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.schedule-time-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
}

.schedule-source-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.schedule-source-badge.source-google {
  background: #e8f4fd;
  color: #1967d2;
}

.schedule-source-badge.source-teams {
  background: #e6f2ff;
  color: #0078d4;
}

.schedule-event-desc {
  margin-top: 10px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  text-align: left;
  word-wrap: break-word;
}

.schedule-list::-webkit-scrollbar {
  width: 6px;
}

.schedule-list::-webkit-scrollbar-track {
  background: #f9fafb;
}

.schedule-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

@media (max-width: 768px) {
  .schedule-header {
    padding: 20px;
  }

  .schedule-header-title {
    font-size: 16px;
  }

  .schedule-date-label {
    font-size: 11px;
    padding: 5px 10px;
  }

  .schedule-list {
    padding: 16px;
    gap: 10px;
    max-height: 400px;
  }

  .schedule-event {
    padding: 16px;
  }

  .schedule-event-title {
    font-size: 14px;
  }

  .schedule-time-badge {
    font-size: 11px;
    padding: 3px 8px;
  }

  .schedule-source-badge {
    font-size: 10px;
    padding: 3px 8px;
  }

  .schedule-event-desc {
    font-size: 12px;
    margin-top: 8px;
  }

  .schedule-event-meta {
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .schedule-header {
    padding: 16px;
  }

  .schedule-header-title {
    font-size: 15px;
  }

  .schedule-list {
    padding: 12px;
    gap: 8px;
  }

  .schedule-event {
    padding: 14px;
  }

  .schedule-check-btn {
    width: 20px;
    height: 20px;
  }
}
.ask-preptune-button {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: #080808ff;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  z-index: 1000;
}

.ask-preptune-button:hover {
  background: #3f3f3f;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

@media (max-width: 768px) {
  .ask-preptune-button {
    bottom: 20px;
    right: 20px;
    padding: 10px 20px;
    font-size: 13px;
  }
}    .chat-input-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 16px;
        margin-top: 20px;
        margin-bottom: 20px;
      }

      .chat-input-inner {
        max-width: 800px;
        width: 100%;
        position: relative;
      }

      .chat-input-container {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(229, 231, 235, 0.8);
        border-radius: 24px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        min-height: 44px;
      }

      .chat-input-container:focus-within {
        border-color: #b3b3b3ff;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .chat-text-input {
        flex: 1;
        min-width: 0;
        border: none;
        background: transparent;
        font-size: 14px;
        color: #111827;
        outline: none;
        padding: 4px 8px;
        font-family: inherit;
      }

      .chat-text-input::placeholder {
        color: #9ca3af;
      }

      .chat-actions-group {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .chat-ask-preptune-btn {
        background: #4091f5;
        border: none;
        color: white;
        padding: 10px 14px;
        border-radius: 16px;
        cursor: pointer;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;
        white-space: nowrap;
        flex-shrink: 0;
      }

.chat-send-btn {
  background: #e6e6e6; /* default gray */
  border: none;
  color: black; /* icon color */
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.chat-send-btn:hover:not(:disabled) {
  background: #3f3f3f;
  color: white;
  transform: translateY(-1px);
}

.chat-send-btn:disabled {
  cursor: default;
  opacity: 0.6;
}

/* Active state when there's input */
.chat-send-btn.active {
  background: #000; /* black */
  color: #fff; /* icon white */
}


      @media (max-width: 768px) {
        .chat-input-wrapper {
          padding: 0 12px;
        }

        .chat-input-container {
          padding: 10px 12px;
          min-height: 40px;
        }

        .chat-text-input {
          font-size: 13px;
        }

        .chat-ask-preptune-btn {
          padding: 8px 10px;
          font-size: 11px;
        }

        .chat-send-btn {
          width: 28px;
          height: 28px;
        }
      }
        @keyframes slideUpFromInput {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes spreadLineLeft {
  to { x2: 0; }
}

@keyframes spreadLineRight {
  to { x2: 600; }
}

@keyframes contractLineLeft {
  to { x2: 300; }
}

@keyframes contractLineRight {
  to { x1: 300; }
}
  .time-styled {
  color: #2563eb;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.3px;
}

.event-time-blue {
  color: #3b82f6;
  font-weight: 600;
  font-size: 12px;
}

.time-display-professional {
  font-size: 12px;
  color: #2563eb;
  padding: 8px 0;
  font-weight: 500;
  letter-spacing: 0.2px;
}
  calendar-container {
  height: 100vh;
  background: linear-gradient(170deg, #e3e3e3d8 0%, #fefefecf 60%, #e0e7fcb2 100%);
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding-top: 80px;
  padding-bottom: 120px; /* ✅ ADD THIS */
  overflow-y: auto;
}

// UPDATE mobile styles:
@media (max-width: 768px) {
  .calendar-container {
    padding: 10px;
    padding-top: 70px;
    padding-bottom: 150px; /* ✅ ADD THIS */
  }
}

@media (max-width: 480px) {
  .calendar-container {
    padding: 8px;
    padding-top: 66px;
    padding-bottom: 180px;
  }
}
  @keyframes bounce {
  0%, 80%, 100% { 
    transform: scale(0);
  }
  40% { 
    transform: scale(1);
  }
}
  
/* Custom scrollbar for message container */
.chat-messages-container::-webkit-scrollbar {
  width: 4px;
}

.chat-messages-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.chat-messages-container::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.3);
  border-radius: 2px;
}

.chat-messages-container::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.5);
}
    @keyframes eventBounce {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes eventUpdate {
    0%, 100% {
      transform: scale(1);
    }
    25% {
      transform: scale(1.15) rotate(-3deg);
    }
    75% {
      transform: scale(1.15) rotate(3deg);
    }
  }

  @keyframes eventDelete {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(0);
      opacity: 0;
    }
  }

  @keyframes eventConfetti {
    0% { 
      transform: translate(-50%, -50%) translate(0, 0) scale(0); 
      opacity: 1; 
    }
    100% { 
      transform: translate(-50%, -50%) translate(var(--confetti-x), var(--confetti-y)) scale(1.5); 
      opacity: 0; 
    }
  }

  .close-chat-button {
    position: absolute;
    top: -40px;
    right: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #000;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease;
    z-index: 100;
  }

  .close-chat-button:hover {
    background: #3f3f3f;
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    .close-chat-button {
      top: -36px;
      width: 28px;
      height: 28px;
    }
  }
      `}</style>

      <div className="calendar-container">
        <div className="calendar-inner">
          {/* Header */}
<div className="header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

    {/* Sync Calendars button */}
    <button
      className="add-event-button"
      onClick={() => setShowSyncModal(true)}
      style={{
        background: 'rgba(255, 255, 255, 0.25)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(238, 238, 238, 0.99)',
        color: '#000',
        borderRadius: '20px',
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontWeight: 500,
        fontSize: '12px',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
        e.currentTarget.style.transform = 'scale(1.03)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {/* Google Calendar Icon */}
      <img 
        src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" 
        alt="Google Calendar" 
        style={{ width: 16, height: 16 }} 
      />

      {/* Microsoft Teams Icon */}
      <img 
        src="https://static.vecteezy.com/system/resources/previews/027/179/374/non_2x/microsoft-teams-icon-logo-symbol-free-png.png" 
        alt="Microsoft Teams" 
        style={{ width: 16, height: 16 }} 
      />

      Sync Calendar
    </button>
    
  </div>
            {/* Add Event button - moved here */}
<button 
  className="today-button"
  onClick={() => openAddModal(new Date())}
  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
>
  <Plus size={14} />
  Add Event
</button>
</div>

          {/* Navigation */}
          
          <div className="nav-container">
            <button
              className="nav-button"
              onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() - 7)))}
            >
              <ChevronLeft size={20} />
            </button>

            <h2 className="date-range">
              {months[weekDays[0].getMonth()]} {weekDays[0].getDate()} - {months[weekDays[6].getMonth()]} {weekDays[6].getDate()}, {weekDays[6].getFullYear()}
            </h2>

            <button
              className="nav-button"
              onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() + 7)))}
            >
              <ChevronRight size={20} />
            </button>
<button
              className="today-button"
              onClick={() => setCurrentDate(new Date())}
            >
              Today
            </button>
            
  
          </div>

          {/* Calendar Grid */}
          <div className="glass-container">
            <div className="calendar-content">
              {/* Day Headers */}
              <div className="day-headers">
                <div className="time-header">Time</div>
                {weekDays.map((day, idx) => (
                  <div 
                    key={idx} 
                    className={`day-header ${day.toDateString() === new Date().toDateString() ? 'today' : ''}`}
                  >
                    <div className="day-name">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.getDay()]}</div>
                    <div className={`day-number ${day.toDateString() === new Date().toDateString() ? 'today-num' : ''}`}>
                      {day.getDate()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="calendar-grid" ref={scrollContainerRef}>
                {/* Time Column */}
                <div className="time-column">
                  {hours.map((hour, idx) => (
                    <div key={idx} className="hour-cell">{hour}</div>
                  ))}
                </div>

                {/* Event Columns */}
{weekDays.map((day, dayIdx) => {
  const dayEvents = getEventsForDate(day);
  
  return (
    <div 
      key={dayIdx} 
      className={`event-column ${day.toDateString() === new Date().toDateString() ? 'today-col' : ''}`}
    >
      {hours.map((hour, hourIdx) => {
        const hoursEvents = dayEvents.filter(e => {
          const eventMin = parseTimeToMinutes(e.time);
          const hourStart = hourIdx * 60;
          const hourEnd = (hourIdx + 1) * 60;
          return eventMin !== null && eventMin >= hourStart && eventMin < hourEnd;
        });

        const isToday = day.toDateString() === new Date().toDateString();

        return (
          <div 
            key={`${formatDateKey(day)}-${hourIdx}`}
            className="event-cell"
onClick={() => openAddModal(day)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, day, hourIdx)}
            style={{ position: 'relative' }}
          >
            {isToday && hourIdx === new Date().getHours() && (
              <div className="current-time-line">
                <div className="current-time-indicator" />
              </div>
            )}

{hoursEvents.map((event) => {
  const color = colors[event.index % colors.length];
  const firstLetter = event.text.charAt(0).toUpperCase();
  
  // ✅ Check if this event is animating
  const addAnimKey = `${event.dateKey}-${event.index}-add`;
  const updateAnimKey = `${event.dateKey}-${event.index}-update`;
  const deleteAnimKey = `${event.dateKey}-${event.index}-delete`;
  
  const isAdding = animatingEvents[addAnimKey];
  const isUpdating = animatingEvents[updateAnimKey];
  const isDeleting = animatingEvents[deleteAnimKey];
  
  const getAnimation = () => {
    if (isDeleting) return 'eventDelete 0.5s ease-out forwards';
    if (isUpdating) return 'eventUpdate 0.6s ease-in-out';
    if (isAdding) return 'eventBounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)';
    return 'none';
  };
  
  return (
    <div
      key={event.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        marginBottom: '8px',
        position: 'relative'
      }}
    >
<div
  className={`event-circle ${event.completed ? 'completed' : ''}`}
  draggable={true}
  onDragStart={(e) => handleDragStart(e, event, day, hourIdx)}
  onDragEnd={() => {
    setIsDragging(false);
    setDraggedEvent(null);
    setDraggedFromCell(null);
  }}
  style={{
    '--circle-color-start': event.completed ? '#9ca3af' : color.bg,
    '--circle-color-end': event.completed ? '#6b7280' : color.border,
    background: event.completed 
      ? 'linear-gradient(135deg, #9ca3af, #6b7280)' 
      : `linear-gradient(135deg, ${color.bg}, ${color.border})`,
    animation: getAnimation(),
    // ✅ Add border for recurring events
    border: event.isRecurringInstance ? '2px solid rgba(59, 130, 246, 0.5)' : '2px solid white',
    boxShadow: event.isRecurringInstance 
      ? '0 3px 8px rgba(0, 0, 0, 0.15), 0 0 0 2px rgba(59, 130, 246, 0.2)' 
      : '0 3px 8px rgba(0, 0, 0, 0.15)'
  }}
  onClick={(e) => {
    e.stopPropagation();
    openEditModal(event);
  }}
>
  {firstLetter}
</div>
      
      {/* ✅ Confetti for add animation */}
      {isAdding && (
        <>
          {[...Array(8)].map((_, i) => (
            <div
              key={`confetti-${i}`}
              style={{
                position: 'absolute',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: ['#22c55e', '#10b981', '#84cc16', '#fbbf24'][i % 4],
                top: '50%',
                left: '50%',
                '--confetti-x': `${Math.cos(i * 45 * Math.PI / 180) * 28}px`,
                '--confetti-y': `${Math.sin(i * 45 * Math.PI / 180) * 28}px`,
                animation: 'eventConfetti 0.6s ease-out forwards',
                pointerEvents: 'none'
              }}
            />
          ))}
        </>
      )}
      
{event.isExternal && (
  <span className={`external-badge ${event.source}`} style={{ fontSize: '8px' }}>
    {event.source === 'google' ? 'G' : 'T'}
  </span>
)}
{/* ✅ Add recurring indicator */}
{event.isRecurringInstance && !event.isExternal && (
  <span style={{
    fontSize: '8px',
    background: 'rgba(59, 130, 246, 0.1)',
    color: '#2563eb',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px'
  }}>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
      <path d="M21 10H3M16 2v4M8 2v4M7.8 22h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 19.72 21 18.88 21 17.2V8.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 4 17.88 4 16.2 4H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 6.28 3 7.12 3 8.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 22 6.12 22 7.8 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
    ↻
  </span>
)}
    </div>
  );
})}
          </div>
        );
      })}
    </div>
  );
})}


              </div>
            </div>
          </div>

<div className="chat-input-wrapper">
  <div className="chat-input-inner" style={{ position: 'relative' }}>
    
    {/* Messages container with auto-scroll */}
{voiceMessages.length > 0 && (
  <div
    style={{
      position: 'absolute',
      bottom: '100%',
      left: 0,
      right: 0,
      marginBottom: '12px',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '360px',
      marginLeft: 'auto',
      marginRight: 'auto',
    }}
  >
    {/* Close button - FIXED at top right, OUTSIDE scrollable area */}
    <button
      onClick={() => {
        setVoiceMessages([]);
        setInputValue('');
        if (messageTimeoutId) {
          clearTimeout(messageTimeoutId);
          setMessageTimeoutId(null);
        }
      }}
      style={{
        position: 'absolute',
        top: '-8px',
        right: '0px',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        background: 'rgba(0, 0, 0, 0.23)',
        border: '2px solid white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        zIndex: 10,
        padding: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
        e.currentTarget.style.transform = 'scale(1.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
        e.currentTarget.style.transform = 'scale(1)';
      }}
      title="Close chat"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path
          d="M18 6L6 18M6 6l12 12"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>

    {/* Scrollable messages container */}
    <div
      ref={(el) => {
        if (el && voiceMessages.length > 0) {
          setTimeout(() => {
            el.scrollTop = el.scrollHeight;
          }, 0);
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        pointerEvents: 'auto',
        padding: '12px',
        borderRadius: '16px',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        animation: 'slideUpFromInput 0.3s ease-out',
        maxHeight: '200px',
        overflowY: 'auto',
        overflowX: 'hidden',
        marginTop: '20px' // Space for the close button above
      }}
    >
      {voiceMessages.map((msg, idx) => (
        <div
          key={msg.id}
          style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
            opacity: 1,
            animation: `fadeIn 0.2s ease-out ${idx * 0.05}s both`
          }}
        >
          {/* Avatar */}
          <div
            style={{
              minWidth: msg.type === 'user' ? '28px' : '72px',
              height: '28px',
              borderRadius: '14px',
              background: msg.type === 'user' ? 'rgba(229, 231, 235, 0.9)' : 'rgba(59, 130, 246, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '600',
              color: msg.type === 'user' ? '#374151' : '#fff',
              flexShrink: 0,
              padding: '0 6px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            {msg.type === 'user' ? 'You' : 'Preptune'}
          </div>

          {/* Message bubble */}
          <div
            style={{
              flex: 1,
              textAlign: 'left',
              background: msg.type === 'user'
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              padding: '8px 14px',
              borderRadius: msg.type === 'user' ? '20px' : '28px',
              fontSize: '12px',
              lineHeight: '1.4',
              color: '#111827',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
              maxWidth: '75%',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              flexWrap: 'wrap',
              wordBreak: 'break-word'
            }}
          >
            {msg.isLoading ? (
              <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#2b2b2b10',
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: '0s'
                }} />
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#2b2b2b10',
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: '0.16s'
                }} />
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#2b2b2b10',
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: '0.32s'
                }} />
              </div>
            ) : (
              <span>{msg.content}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
)}
 
    {/* Waveform - also inside container */}
    {isVoiceMode && (
      <div style={{
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '8px',
        width: '100%',
        maxWidth: '400px',
        height: '20px',
        pointerEvents: 'none'
      }}>
        <svg width="100%" height="20" viewBox="0 0 600 20" preserveAspectRatio="none">
          {!waveformData.isActive && waveformData.points === '' && (
            <>
              <line x1="300" y1="10" x2="300" y2="10" stroke="#2b2b2b10" strokeWidth="2" strokeLinecap="round"
                style={{ animation: 'spreadLineLeft 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }} />
              <line x1="300" y1="10" x2="300" y2="10" stroke="#2b2b2b10" strokeWidth="2" strokeLinecap="round"
                style={{ animation: 'spreadLineRight 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }} />
            </>
          )}
          
          {waveformData.isActive && waveformData.points && (
            <polyline 
              points={waveformData.points} 
              fill="none" 
              stroke="#2b2b2b10" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          )}
          
          {!waveformData.isActive && waveformData.points !== '' && (
            <>
              <line x1="0" y1="10" x2="300" y2="10" stroke="#2b2b2b10" strokeWidth="2" strokeLinecap="round"
                style={{ animation: 'contractLineLeft 1.2s forwards' }} />
              <line x1="300" y1="10" x2="600" y2="10" stroke="#2b2b2b10" strokeWidth="2" strokeLinecap="round"
                style={{ animation: 'contractLineRight 1.2s forwards' }} />
            </>
          )}
        </svg>
      </div>
    )}

    {/* Input container with buttons */}
    <div className="chat-input-container">

<input
  type="text"
  className="chat-text-input"
  placeholder={
    isVoiceMode 
      ? "Voice mode active - or type here..." 
      : "Plan with Preptune..."
  }
  value={inputValue}
  onChange={(e) => setInputValue(e.target.value)}
  onKeyPress={(e) => {
    if (e.key === 'Enter' && !isVoiceMode && !isSendingMessage && inputValue.trim()) {
      handleSendMessage();
    }
  }}
  disabled={isSendingMessage} // ✅ CHANGED: Only disable while sending
/>


      <div className="chat-actions-group">
{/* ✅ VOICE CONTROLS - Floating Group */}
<div style={{
  position: 'relative',

  zIndex: 999,
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
}}>
  
  {/* 🎙️ VOICE MODE BUTTON */}
  <button 
    className="chat-ask-preptune-btn"
    onClick={async () => {
      if (isVoiceMode) {
        await stopVoiceMode();
      } else if (!isInitializingVoice) {
        await initializeVoiceMode();
      }
    }}
    disabled={isInitializingVoice || isSendingMessage}
    style={
      isVoiceMode 
        ? {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            boxShadow: '0 0 10px rgba(239, 68, 68, 0.4)',
          }
        : isInitializingVoice 
          ? {
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 0 10px rgba(59, 130, 246, 0.4)',
              cursor: 'wait'
            }
          : {}
    }
  >
    {isInitializingVoice ? (
      <>
        <div style={{
          width: '14px',
          height: '14px',
          border: '2px solid rgba(255,255,255,0.3)',
          borderTopColor: 'white',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        {voiceStatus || 'Starting...'}
      </>
    ) : isVoiceMode ? (
      <>
        <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Z"/>
        </svg>
        End Call
      </>
    ) : (
      <>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9v3a5.006 5.006 0 0 1-5 5h-4a5.006 5.006 0 0 1-5-5V9m7 9v3m-3 0h6M11 3h2a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z"/>
        </svg>
        Voice Mode
      </>
    )}
  </button>

  {/* 🔇 MUTE BUTTON - Only show when in voice mode */}
  {isVoiceMode && (
    <button 
      className="chat-ask-preptune-btn"
      onClick={() => {
        if (voiceManager) {
          voiceManager.toggleMicrophone(!micMuted);
          setMicMuted(!micMuted);
        }
      }}
      style={{
        background: '#f5f5f5ff',
        boxShadow: '0 4px 5px rgba(0, 0, 0, 0.2)',
        padding: '10px 14px',
        minWidth: 'auto',
        width: 'auto',
        borderRadius:'50px'
      }}
      title={micMuted ? 'Unmute microphone' : 'Mute microphone'}
    >
      {micMuted ? (
        // Muted icon (mic with slash)
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
          <line x1="1" y1="1" x2="23" y2="23"></line>
          <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
          <path d="M17 16.95A7 7 0 0 1 5 12m14 0a7 7 0 0 1-13.46 3"></path>
        </svg>
      ) : (
        // Unmuted icon (mic normal)
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
          <line x1="12" y1="19" x2="12" y2="23"></line>
          <line x1="8" y1="23" x2="16" y2="23"></line>
        </svg>
      )}
    </button>
  )}
</div>


        {/* Send button - Only show when NOT in voice mode */}
        {!isVoiceMode && (
          <button 
            className={`chat-send-btn ${inputValue.trim() && !isSendingMessage ? 'active' : ''}`}
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isSendingMessage}
            style={{
              cursor: isSendingMessage ? 'wait' : (!inputValue.trim() ? 'default' : 'pointer')
            }}
          >
            {isSendingMessage ? (
              <div style={{
                width: '15px',
                height: '15px',
                border: '2px solid rgba(0,0,0,0.2)',
                borderTopColor: '#000',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
            ) : (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path d="M3.13523 8.84197C3.3241 9.04343 3.64052 9.05363 3.84197 8.86477L7.5 5.43536L11.158 8.86477C11.3595 9.05363 11.6759 9.04343 11.8648 8.84197C12.0536 8.64051 12.0434 8.32409 11.842 8.13523L7.84197 4.38523C7.64964 4.20492 7.35036 4.20492 7.15803 4.38523L3.15803 8.13523C2.95657 8.32409 2.94637 8.64051 3.13523 8.84197Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
              </svg>
            )}
          </button>
        )}
      </div>
    </div>

  </div>
</div>

{/* Today's Schedule */}
<div className="glass-container schedule-wrapper">
  <div className="schedule-container">
    {/* Header */}
    <div className="schedule-header">
      <div className="schedule-header-row">
        <h2 className="schedule-header-title">Today's Schedule</h2>
        <div className="schedule-date-label">
          {formatDate(new Date())}
        </div>
      </div>
    </div>

    {/* Content */}
    {todayEvents.length === 0 ? (
      <div className="schedule-empty">
        <p className="schedule-empty-text">No events scheduled for today</p>
      </div>
    ) : (
      <div className="schedule-list">
        {todayEvents.map((event) => {
          const color = colors[event.index % colors.length];
          
          return (
            <div
              key={event.id}
              className={`schedule-event ${event.completed ? 'is-completed' : ''}`}
              style={{
                borderLeftColor: event.completed ? '#d1d5db' : color.border
              }}
 onClick={() => openEditModal(event)}
            >
              {/* Header Row */}
              <div className="schedule-event-header">
                <div className={`schedule-event-title ${event.completed ? 'is-completed' : ''}`}>
                  {event.text}
                </div>

                {/* Checkbox on right */}
                <div
                  className={`schedule-check-btn ${event.completed ? 'is-checked' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleTodayEvent(event.dateKey, event.index);
                  }}
                >
                  {event.completed && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6L5 9L10 3"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Meta Row */}
              <div className="schedule-event-meta">
                {/* Time */}
                <div className="schedule-time-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {formatTime12Hour(event.time)}
                </div>

                {/* Source */}
                {event.isExternal && (
                  <span className={`schedule-source-badge source-${event.source}`}>
                    {event.source === 'google' ? 'Google' : 'Teams'}
                  </span>
                )}
              </div>

              {/* Description */}
              {event.description && (
                <div className="schedule-event-desc">
                  {event.description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
</div>

{showAddEditModal && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.3)',
    zIndex: 999,
    display: 'flex',
    alignItems: 'flex-end',
    backdropFilter: 'blur(4px)'
  }} onClick={() => setShowAddEditModal(false)}>
    <div
          onClick={(e) => e.stopPropagation()}  
     style={{
      width: '100%',
      maxHeight: '88vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      zIndex: 1000,
      animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      
      {/* Header */}
      <div style={{
        padding: '20px 20px 16px 20px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div style={{ textAlign: 'left', flex: 1 }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            marginBottom: '4px'
          }}>
            {isEditMode ? 'Edit Event' : 'Add New Event'}
          </h2>
          <p style={{
            fontSize: '12px',
            color: '#9ca3af',
            margin: 0,
            lineHeight: '1.4'
          }}>
            {isEditMode ? 'Update your event details' : 'Create a new event'}
          </p>
        </div>
        <button
          onClick={() => setShowAddEditModal(false)}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s',
            flexShrink: 0,
            marginLeft: '8px',
            padding: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f3f4f6';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <X size={17} color="#1f2937" />
        </button>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        
        {/* Title Input */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            textAlign:'left',
          }}>
            Event Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Enter event title"
            autoFocus
            style={{
              width: '100%',
              padding: '12px 14px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#272727';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(39, 39, 39, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Time Picker */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '12px',
         textAlign:'left',
          }}>
            Time
          </label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            justifyContent: 'center'
          }}>
            {/* Hours */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setFormData({ ...formData, hours: formData.hours === 1 ? 12 : formData.hours - 1 })}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  padding: '6px 10px',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
              >
                −
              </button>
              <div style={{
                minWidth: '50px',
                padding: '8px 12px',
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600',
                color: '#3b79ffff'
              }}>
                {formData.hours.toString().padStart(2, '0')}
              </div>
              <button
                onClick={() => setFormData({ ...formData, hours: formData.hours === 12 ? 1 : formData.hours + 1 })}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  padding: '6px 10px',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
              >
                +
              </button>
            </div>

            {/* Separator */}
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#d1d5db' }}>:</div>

            {/* Minutes */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setFormData({ ...formData, minutes: formData.minutes === 0 ? 45 : formData.minutes - 15 })}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  padding: '6px 10px',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
              >
                −
              </button>
              <div style={{
                minWidth: '50px',
                padding: '8px 12px',
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600',
                color: '#3b79ffff'
              }}>
                {formData.minutes.toString().padStart(2, '0')}
              </div>
              <button
                onClick={() => setFormData({ ...formData, minutes: formData.minutes === 45 ? 0 : formData.minutes + 15 })}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  padding: '6px 10px',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
              >
                +
              </button>
            </div>

            {/* AM/PM Toggle */}
            <button
              onClick={() => setFormData({ ...formData, ampm: formData.ampm === 'AM' ? 'PM' : 'AM' })}
              style={{
                background: '#f3f4f6',
                border: '1px solid #e5e7eb',
                padding: '8px 14px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                minWidth: '60px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
              }}
            >
              {formData.ampm}
            </button>
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
           textAlign:'left',
          }}>
            Description (Optional)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Add event details..."
            style={{
              width: '100%',
              padding: '12px 14px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              minHeight: '100px',
              resize: 'vertical',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#272727';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(39, 39, 39, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      {/* Footer Buttons */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e7eb',
        background: '#fafafa',
        display: 'flex',
        gap: '12px'
      }}>
        {isEditMode && selectedEvent && (
          <button
            onClick={handleDeleteEvent}
            style={{
              padding: '8px 16px',
              background: '#eaeaeaff',
              color: '#000000ff',
              border: 'none',
              borderRadius: '24px',
                      flex:1,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fecaca';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fee2e2';
            }}
          >
            Delete
          </button>
        )}
        <button
          onClick={handleSaveEvent}
          style={{
            flex: 1,
            padding: '10px 8px',
            background: '#000000ff',
            color: 'white',
            border: 'none',
            borderRadius: '24px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#3f3f3f';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#272727';
          }}
        >
          <Plus size={14} />
          {isEditMode ? 'Update Event' : 'Add Event'}
        </button>
      </div>

      <style>{`
        @keyframes slideUp {
          from { 
            transform: translateY(100%);
            opacity: 0;
          }
          to { 
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  </div>
)}


{/* Reschedule Modal */}
{showDragModal && draggedEvent && (
  <div className="modal-overlay" onClick={() => setShowDragModal(false)}>
    <div className="modal drag-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3 className="modal-title" style={{ fontSize: '15px' }}>Reschedule: {draggedEvent.text}</h3>
        <button className="close-button" onClick={() => setShowDragModal(false)}>
          <X size={18} />
        </button>
      </div>

      <div className="time-picker-container" style={{ margin: '16px 0' }}>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
          Select new time
        </p>
        
        {/* Hour Spinner */}
        <div style={{ marginBottom: '12px' }}>
          <label className="form-label" style={{ textAlign: 'center', display: 'block', fontSize: '11px' }}>Hour</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
            <button
              className="nav-button"
              style={{ padding: '6px' }}
              onClick={() => {
                const currentMins = parseTimeToMinutes(draggedEvent.time) || 0;
                const hours = Math.floor(currentMins / 60);
                const minutes = currentMins % 60;
                const newHours = hours === 0 ? 23 : hours - 1;
                handleDragTimeChange(newHours * 60 + minutes);
              }}
            >
              <ChevronLeft size={18} />
            </button>
            
            <div style={{
              minWidth: '90px',
              padding: '10px 16px',
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '15px',
              fontWeight: '600',
              color: '#667eea'
            }}>
              {(() => {
                const currentMins = parseTimeToMinutes(draggedEvent.time) || 0;
                const hours = Math.floor(currentMins / 60);
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const displayHour = hours % 12 || 12;
                return `${displayHour} ${ampm}`;
              })()}
            </div>
            
            <button
              className="nav-button"
              style={{ padding: '6px' }}
              onClick={() => {
                const currentMins = parseTimeToMinutes(draggedEvent.time) || 0;
                const hours = Math.floor(currentMins / 60);
                const minutes = currentMins % 60;
                const newHours = hours === 23 ? 0 : hours + 1;
                handleDragTimeChange(newHours * 60 + minutes);
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Minutes Spinner */}
        <div style={{ marginBottom: '16px' }}>
          <label className="form-label" style={{ textAlign: 'center', display: 'block', fontSize: '11px' }}>Minutes</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
            <button
              className="nav-button"
              style={{ padding: '6px' }}
              onClick={() => {
                const currentMins = parseTimeToMinutes(draggedEvent.time) || 0;
                const hours = Math.floor(currentMins / 60);
                const minutes = currentMins % 60;
                const newMinutes = minutes === 0 ? 45 : minutes - 15;
                handleDragTimeChange(hours * 60 + newMinutes);
              }}
            >
              <ChevronLeft size={18} />
            </button>
            
            <div style={{
              minWidth: '90px',
              padding: '10px 16px',
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '15px',
              fontWeight: '600',
              color: '#667eea'
            }}>
              {(() => {
                const currentMins = parseTimeToMinutes(draggedEvent.time) || 0;
                const minutes = currentMins % 60;
                return `:${minutes.toString().padStart(2, '0')}`;
              })()}
            </div>
            
            <button
              className="nav-button"
              style={{ padding: '6px' }}
              onClick={() => {
                const currentMins = parseTimeToMinutes(draggedEvent.time) || 0;
                const hours = Math.floor(currentMins / 60);
                const minutes = currentMins % 60;
                const newMinutes = minutes === 45 ? 0 : minutes + 15;
                handleDragTimeChange(hours * 60 + newMinutes);
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Preview */}
        <div style={{ 
          padding: '10px', 
          background: 'rgba(102, 126, 234, 0.1)', 
          borderRadius: '8px',
          textAlign: 'center',
          fontSize: '13px',
          fontWeight: '600',
          color: '#667eea'
        }}>
          {formatTime12Hour(draggedEvent.time)}
        </div>
      </div>

      {/* Bottom Actions - Today left, Confirm right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
        <button 
          className="cancel1-button"
          onClick={() => {
            const now = new Date();
            const hours = now.getHours();
            const minutes = Math.floor(now.getMinutes() / 15) * 15;
            handleDragTimeChange(hours * 60 + minutes);
          }}
        >
          Today
        </button>
        
        <button 
          className="submit1-button"
          onClick={() => setShowDragModal(false)}
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
)}

        </div>
        <CalendarSyncModal
  isOpen={showSyncModal}
  onClose={() => setShowSyncModal(false)}
  onSyncComplete={handleSyncComplete}
/>
      </div>
    </>
  );
}